import { faPrint } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { calculateBalanceDueDate } from '@website/components/checkout/subComponents/CheckoutPrices';
import { DateTime } from 'luxon';
import Link from 'next/link';
import { Badge, Button, Table } from 'react-bootstrap';
import {
  BookingSpaceInvoiceLineType,
  UserBookingDetailsQuery,
} from '../../../../types/graphqlOperations';
import AdminInvoiceSection from './InvoicesSections/AdminInvoiceSection';
import BalancePaymentSection from './InvoicesSections/BalancePaymentSection';
import TotalBookingValueSection from './InvoicesSections/TotalBookingValueSection';
import TransactionsSection from './InvoicesSections/TransactionsSection';
import DiscretionaryRefund from './cancel/DiscretionaryRefund';

/**
 * Get Balance Outstanding
 */
export function getBalanceOutstanding(booking: {
  spaces: Pick<
    UserBookingDetailsQuery['booking']['spaces'][0],
    'guestBalance'
  >[];
}): number {
  return booking.spaces.reduce((balanceOutstandingAccumulator, space) => {
    const newBalanceOutstandingAccumulator =
      balanceOutstandingAccumulator + space.guestBalance.amountZD;
    return newBalanceOutstandingAccumulator;
  }, 0);
}

/**
 * Sum all guest amounts (across spaces) of a specific invoice line type
 */
export function sumGuestAmounts(
  booking: {
    spaces: Pick<UserBookingDetailsQuery['booking']['spaces'][0], 'invoice'>[];
  },
  type: BookingSpaceInvoiceLineType,
) {
  return booking.spaces.reduce(
    (
      spaceAccumulator: number,
      space: UserBookingDetailsQuery['booking']['spaces'][0],
    ) => {
      const matchingInvoiceLines = space.invoice.filter((i) => i.type === type);
      const spaceInvoiceLinesSum = matchingInvoiceLines.reduce(
        (
          lineAccumulator: number,
          line: UserBookingDetailsQuery['booking']['spaces'][0]['invoice'][0],
        ) => line.guestAmount.amountZD + lineAccumulator,
        0,
      );
      return spaceAccumulator + spaceInvoiceLinesSum;
    },
    0,
  );
}

/**
 * Get Transactions
 */
export function getTransactions(booking: {
  spaces: Pick<UserBookingDetailsQuery['booking']['spaces'][0], 'invoice'>[];
}) {
  const transactions = [];

  // Map through each space and get the invoice transaction items and create an array of the sub transaction items if
  // successful
  booking.spaces.map((space) => {
    const invoiceTransactions = space.invoice.filter(
      (invoiceItem) =>
        invoiceItem.type === BookingSpaceInvoiceLineType.TRANSACTION,
    );

    invoiceTransactions
      .filter(
        (invoiceTransactionItem) =>
          invoiceTransactionItem.transaction?.success === true,
      )
      .map((item) => transactions.push(item.transaction));
    return null;
  });

  // Get only unique transactions by their ids
  const ids = transactions.map((o) => o.id);
  const uniqueTransactions = transactions.filter(
    ({ id }, index) => !ids.includes(id, index + 1),
  );

  // Time sort
  return uniqueTransactions.sort(
    (a, b) =>
      DateTime.fromISO(a.created).toSeconds() -
      DateTime.fromISO(b.created).toSeconds(),
  );
}

export function getCreditLines(
  booking: Pick<
    UserBookingDetailsQuery['booking'],
    'id' | 'created' | 'user'
  > & {
    group: Pick<UserBookingDetailsQuery['booking']['group'], 'start'>;
    spaces: Pick<
      UserBookingDetailsQuery['booking']['spaces'][0],
      'invoice' | 'guestBalance' | 'active'
    >[];
  },
) {
  const cancellationsLines = booking.spaces.filter((space) => {
    return (
      !space.active &&
      space.invoice.filter(
        (line) => line.type === BookingSpaceInvoiceLineType.CREDIT,
      ).length > 0
    );
  });

  const totalCancellation = cancellationsLines.reduce(
    (acc, space) =>
      acc +
      space.invoice.reduce((acc2, line) => {
        return line.type === BookingSpaceInvoiceLineType.CREDIT
          ? acc2 + line.guestAmount.amountZD
          : acc2;
      }, 0),
    0,
  );

  const nonCancellationLine = booking.spaces.filter(
    (space) =>
      space.active &&
      space.invoice.filter(
        (line) => line.type === BookingSpaceInvoiceLineType.CREDIT,
      ).length > 0,
  );

  const totalNonCancellation = nonCancellationLine.reduce(
    (acc, space) =>
      acc +
      space.invoice.reduce((acc2, line) => {
        return line.type === BookingSpaceInvoiceLineType.CREDIT
          ? acc2 + line.guestAmount.amountZD
          : acc2;
      }, 0),
    0,
  );

  return {
    creditsReturned: totalCancellation,
    creditsUsed: totalNonCancellation,
  };
}

export function getTransactionLines(
  booking: Pick<
    UserBookingDetailsQuery['booking'],
    'id' | 'created' | 'user'
  > & {
    group: Pick<UserBookingDetailsQuery['booking']['group'], 'start'>;
    spaces: Pick<
      UserBookingDetailsQuery['booking']['spaces'][0],
      'invoice' | 'guestBalance' | 'active'
    >[];
  },
) {
  const cancellationsLines = booking.spaces.filter((space) => {
    return (
      !space.active &&
      space.invoice.filter(
        (line) => line.type === BookingSpaceInvoiceLineType.CANCELLATION,
      ).length > 0
    );
  });

  return {
    date: cancellationsLines[0]?.invoice[0]?.created,
    cancellations: cancellationsLines.reduce(
      (acc, space) =>
        acc +
        space.invoice.reduce((acc2, line) => {
          return line.type === BookingSpaceInvoiceLineType.CANCELLATION
            ? acc2 + line.guestAmount.amountZD
            : acc2;
        }, 0),
      0,
    ),
  };
}

export function BookingInvoiceInner({
  booking,
  hideAdminInvoice = false,
}: {
  booking: Pick<
    UserBookingDetailsQuery['booking'],
    'created' | 'commissionDetails' | 'invoiceSummary' | 'spaces' | 'group'
  >;
  hideAdminInvoice?: boolean;
}) {
  const balanceDueDate = calculateBalanceDueDate(booking.group.start.utc);

  return (
    <>
      <Table className="w-100" borderless size="sm">
        <tbody>
          <TotalBookingValueSection booking={booking} />

          <TransactionsSection booking={booking} />

          <BalancePaymentSection
            balanceDueDate={balanceDueDate}
            booking={booking}
          />
        </tbody>
      </Table>
      {!hideAdminInvoice && <AdminInvoiceSection booking={booking} />}
    </>
  );
}

/**
 * User Booking Details - Booking Invoice
 */
export default function BookingInvoice({
  booking,
  isInFuture,
  isBookingActive,
}: {
  booking: Pick<
    UserBookingDetailsQuery['booking'],
    | 'group'
    | 'spaces'
    | 'id'
    | 'created'
    | 'user'
    | 'commissionDetails'
    | 'invoiceSummary'
  >;
  isInFuture: boolean;
  isBookingActive: boolean;
}) {
  // Balance
  const balanceOutstanding = getBalanceOutstanding(booking);

  // Status
  const cancelled = !isBookingActive;
  const fullyPaid = isBookingActive && balanceOutstanding === 0;
  let status = 'Balance Outstanding';
  if (fullyPaid) status = 'Fully paid';
  if (cancelled) status = 'Cancelled';

  return (
    <div className="text-block">
      <div className="mb-3">
        <div>
          <h5>Invoice</h5>

          {isInFuture && (
            <Badge pill bg={fullyPaid ? 'success' : 'warning'} className="mb-3">
              {status}
            </Badge>
          )}

          <BookingInvoiceInner booking={booking} />

          <div className="pt-4">
            {balanceOutstanding > 0 && isInFuture && (
              <Link
                href={`/me/${booking.user.id}/bookings/${booking.id}/pay`}
                legacyBehavior
              >
                <Button className="mb-2 me-2">Pay Balance</Button>
              </Link>
            )}

            {isInFuture && isBookingActive && (
              <Link
                href={`/me/${booking.user.id}/bookings/${booking.id}/cancel`}
                legacyBehavior
              >
                <Button className="mb-2 me-2">Cancel / Change Date</Button>
              </Link>
            )}

            <Link
              href={`/me/${booking.user.id}/bookings/${booking.id}/invoice`}
              legacyBehavior
            >
              <Button className="mb-2 me-2">
                <FontAwesomeIcon icon={faPrint} /> Invoice
              </Button>
            </Link>

            <Link
              href={`/me/${booking.user.id}/bookings/${booking.id}/visa-letter`}
              legacyBehavior
            >
              <Button className="mb-2 me-2">Visa Letter</Button>
            </Link>

            <DiscretionaryRefund bookingID={booking.id} />
          </div>
        </div>
      </div>
    </div>
  );
}
